import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ArrowRight, Users, Heart, Award, TrendingUp, X, User, Phone, Mail, MessageSquare, Loader2, Send, CheckCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useImages } from '../hooks/useImages';

const API = process.env.REACT_APP_BACKEND_URL;

const fadeUp = { hidden: { opacity: 0, y: 28 }, visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.12, ease: [0.25, 0.46, 0.45, 0.94] } }) };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.12 } } };

const ApplyForm = ({ open, onClose }) => {
  const [form, setForm] = useState({ name: '', contact: '', email: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await fetch(`${API}/api/consult`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, source: 'WHeros - Apply Now' })
      });
    } catch {}
    setLoading(false);
    setSubmitted(true);
    setTimeout(() => { setSubmitted(false); setForm({ name: '', contact: '', email: '', message: '' }); onClose(); }, 2500);
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.3 }}
          className="relative w-full max-w-md z-10"
        >
          <Card className="border border-[#E8DFD3] shadow-2xl bg-white overflow-hidden">
            <div className="bg-gradient-to-r from-[#C4844A] to-[#A96E3A] px-6 py-5 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>Apply Now</h3>
                <p className="text-xs text-white/70 mt-0.5">WHeros Empowerment Program</p>
              </div>
              <button onClick={onClose} className="p-1.5 hover:bg-white/15 rounded-lg transition-colors cursor-pointer" data-testid="close-apply-form">
                <X size={18} className="text-white" />
              </button>
            </div>
            <CardContent className="p-6">
              {submitted ? (
                <div className="text-center py-6" data-testid="apply-success">
                  <CheckCircle size={48} className="mx-auto text-[#5C7A3A] mb-3" />
                  <h4 className="text-lg font-bold text-[#2C1810] mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>Application Received!</h4>
                  <p className="text-sm text-[#7a6555]">We'll review and get back to you within 48 hours.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-[#2C1810] mb-1.5 flex items-center gap-1.5">
                      <User size={13} className="text-[#7a6555]" /> Full Name
                    </label>
                    <Input data-testid="apply-name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Your full name" required />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#2C1810] mb-1.5 flex items-center gap-1.5">
                      <Phone size={13} className="text-[#7a6555]" /> Contact Number
                    </label>
                    <Input data-testid="apply-contact" type="tel" value={form.contact} onChange={e => { const val = e.target.value.replace(/\D/g, '').slice(0, 10); setForm(f => ({ ...f, contact: val })); }} placeholder="10-digit mobile number" pattern="\d{10}" maxLength={10} required />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#2C1810] mb-1.5 flex items-center gap-1.5">
                      <Mail size={13} className="text-[#7a6555]" /> Email
                    </label>
                    <Input data-testid="apply-email" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="your@email.com" required />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#2C1810] mb-1.5 flex items-center gap-1.5">
                      <MessageSquare size={13} className="text-[#7a6555]" /> Message
                    </label>
                    <Textarea data-testid="apply-message" value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} placeholder="Tell us about yourself, your skills, and what program you're interested in..." rows={3} required />
                  </div>
                  <Button type="submit" disabled={loading} className="w-full bg-[#C4844A] hover:bg-[#A96E3A] text-white font-semibold py-5 cursor-pointer" data-testid="submit-apply-btn">
                    {loading ? <><Loader2 size={16} className="animate-spin mr-2" /> Submitting...</> : <><Send size={16} className="mr-2" /> Submit Application</>}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

const WHeros = () => {
  const siteImages = useImages();
  const img = (slug, fb) => siteImages[slug] || fb;
  const [showApply, setShowApply] = useState(false);

  const programs = [
    { icon: Users, title: 'Skill Development Hubs', desc: 'Training in food processing, packaging, and entrepreneurship' },
    { icon: Heart, title: 'Community Kitchens', desc: 'Shared production facilities for women entrepreneurs' },
    { icon: Award, title: 'Certification Programs', desc: 'Industry-recognized training and certifications' },
    { icon: TrendingUp, title: 'Livelihood Support', desc: 'Income generation and financial independence' }
  ];

  const impact = [
    { number: '500+', label: 'Women Trained' },
    { number: '120+', label: 'Businesses Launched' },
    { number: '15', label: 'Skill Centers' },
    { number: '2Cr+', label: 'Income Generated' }
  ];

  return (
    <div className="min-h-screen" style={{ background: '#FDF8F0' }}>
      <ApplyForm open={showApply} onClose={() => setShowApply(false)} />

      {/* Hero */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#D4A574]/8 to-[#5C7A3A]/5"></div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" initial="hidden" animate="visible" variants={stagger}>
            <div className="space-y-6">
              <motion.span variants={fadeUp} custom={0} className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-[#C4844A] bg-[#C4844A]/8 px-4 py-2 rounded-full border border-[#C4844A]/15">
                Women Empowerment &middot; Skill Building &middot; Independence
              </motion.span>
              <motion.h1 variants={fadeUp} custom={1} className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight text-[#2C1810]" style={{ fontFamily: 'Playfair Display, serif' }}>
                W<span className="text-[#C4844A]">Heros</span>
              </motion.h1>
              <motion.p variants={fadeUp} custom={2} className="text-base text-[#5a4535] leading-relaxed max-w-lg">
                Empowering women to become changemakers in the food ecosystem. Through skills training, community support, and entrepreneurship programs, we're building a movement of women leaders.
              </motion.p>
              <motion.div variants={fadeUp} custom={3} className="flex flex-col sm:flex-row gap-3">
                <Button size="lg" onClick={() => setShowApply(true)} className="bg-[#C4844A] hover:bg-[#A96E3A] text-white font-semibold px-8 py-5 rounded-lg shadow-lg shadow-[#C4844A]/20 transition-all duration-300 hover:scale-105 cursor-pointer" data-testid="wheros-apply-btn">
                  Apply Now <ArrowRight className="ml-2" size={18} />
                </Button>
                <Button size="lg" variant="outline" onClick={() => setShowApply(true)} className="border-2 border-[#C4844A] text-[#C4844A] hover:bg-[#C4844A] hover:text-white font-semibold px-8 py-5 rounded-lg transition-all duration-300 hover:scale-105 cursor-pointer" data-testid="wheros-partner-btn">
                  Partner With Us
                </Button>
              </motion.div>
            </div>
            <motion.div variants={fadeUp} custom={2}>
              <img src={img('wheros-hero', 'https://images.unsplash.com/photo-1646066490017-c935b1a1eb0f')} alt="WHeros empowerment" className="w-full h-80 object-cover rounded-2xl shadow-xl shadow-[#C4844A]/10" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Programs */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-8" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="flex items-center gap-5 justify-center mb-3">
              <div className="h-px bg-gradient-to-r from-transparent to-[#D4A574] w-16"></div>
              <Heart size={16} className="text-[#D4A574]" />
              <div className="h-px bg-gradient-to-l from-transparent to-[#D4A574] w-16"></div>
            </motion.div>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-[#2C1810]" style={{ fontFamily: 'Playfair Display, serif' }}>Our Programs</motion.h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {programs.map((p, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.1, duration: 0.5 }}>
                <Card className="group border border-[#E8DFD3] shadow-sm hover:shadow-lg hover:shadow-[#D4A574]/10 transition-all duration-500 hover:-translate-y-2 text-center bg-[#FDF8F0]" data-testid={`program-${idx}`}>
                  <CardContent className="p-6">
                    <div className="bg-[#C4844A]/10 p-3.5 rounded-2xl inline-block mb-4 group-hover:bg-[#C4844A]/15 transition-colors duration-300">
                      <p.icon size={28} className="text-[#C4844A]" />
                    </div>
                    <h3 className="text-lg font-bold text-[#2C1810] mb-2 group-hover:text-[#C4844A] transition-colors duration-300">{p.title}</h3>
                    <p className="text-sm text-[#7a6555]">{p.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="relative py-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#2C1810] to-[#4a3020]"></div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl sm:text-4xl font-bold text-white mb-8 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>Our <span className="text-[#D4A574]">Impact</span></motion.h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {impact.map((stat, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.1, duration: 0.5 }} className="text-center" data-testid={`impact-${idx}`}>
                <p className="text-4xl sm:text-5xl font-bold text-[#D4A574] mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>{stat.number}</p>
                <p className="text-sm text-white/70 font-medium">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-12" style={{ background: '#FDF8F0' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl sm:text-4xl font-bold text-[#2C1810] mb-8 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>Success Stories</motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { img: img('wheros-story-1', 'https://images.unsplash.com/photo-1707721691170-bf913a7a6231'), title: 'From Farmer to Entrepreneur', quote: 'WHeros training gave me the confidence to start my own organic food processing unit. Today, I employ 10 women from my village.', name: 'Meena Devi, Hyderabad' },
              { img: img('wheros-story-2', 'https://images.unsplash.com/photo-1707721690626-10e5f0366bcb'), title: 'Building Financial Freedom', quote: 'The community kitchen program helped me launch my catering business. I\'m now financially independent and supporting my family.', name: 'Kavita Sharma, Bangalore' }
            ].map((story, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.15, duration: 0.5 }}>
                <Card className="group border border-[#E8DFD3] shadow-md hover:shadow-xl transition-all duration-500 overflow-hidden bg-white" data-testid={`story-${idx}`}>
                  <div className="relative h-56 overflow-hidden">
                    <img src={story.img} alt={story.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#2C1810]/40 to-transparent"></div>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-[#2C1810] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>{story.title}</h3>
                    <p className="text-sm text-[#5a4535] leading-relaxed mb-3 italic">"{story.quote}"</p>
                    <p className="text-sm text-[#C4844A] font-semibold">- {story.name}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-14 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#2C4A1A] via-[#3B5A2B] to-[#2A3F1E]"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Join the <span className="text-[#D4A574]">WHeros Movement</span></h2>
            <p className="text-base text-white/75 mb-8 max-w-md mx-auto">Apply for training, volunteer, or partner with us to empower more women</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" onClick={() => setShowApply(true)} className="bg-[#D4A574] hover:bg-[#C4844A] text-white font-semibold px-8 py-5 rounded-lg shadow-lg transition-all duration-300 hover:scale-105 cursor-pointer" data-testid="wheros-apply-cta-btn">Apply for Training</Button>
              <Button size="lg" variant="outline" onClick={() => setShowApply(true)} className="border-2 border-white text-white hover:bg-white hover:text-[#2C1810] font-semibold px-8 py-5 rounded-lg transition-all duration-300 hover:scale-105 cursor-pointer" data-testid="wheros-volunteer-btn">Become a Volunteer</Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default WHeros;
